/* eslint-disable react-hooks/exhaustive-deps */
import { SelectChangeEvent } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { MerChantModel } from '../../../models/merChantModel';
import axios from '../../../lib/axios';
import { MERCHANT_STORE_FIND_MANY } from '../../../lib/endpoints/merchants';
import { useTranslation } from 'react-i18next';
import { DeliveryFeeModel } from '../../../models/deliveryFee';
import { CREATE_DELIVERY_FEE, UPDATE_DELIVERY_FEE } from '../../../lib/endpoints/deliveryFee';

interface Props {
  onClose: Function;
  selectedData: DeliveryFeeModel;
  setSelectedData: Function;
  onFetchData: Function;
}

export const useFormDeliveryFeeController = ({
  selectedData,
  onClose,
  setSelectedData,
  onFetchData,
}: Props) => {
  const [distance, setDistance] = useState('');
  const [loadingConfirmButton, setLoadingConfirmButton] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(null!);

  const areaName = useRef<HTMLInputElement>(null!);
  const basePrice = useRef<HTMLInputElement>(null!);
  const extraPrice = useRef<HTMLInputElement>(null!);
  const limitDistance = useRef<HTMLInputElement>(null!);
  const extraDistance = useRef<HTMLInputElement>(null!);

  const { t } = useTranslation('global');

  const handleSelectedDistance = (event: SelectChangeEvent) => {
    setDistance(event.target.value as string);
  };

  const handleValidateForm = () => {
    if (!areaName.current?.value) {
      setErrorMessage('Please enter area name');
      return false;
    }
    const basePriceValue = parseInt(basePrice.current?.value);
    if (!basePriceValue || isNaN(basePriceValue) || basePriceValue < 1000) {
      setErrorMessage('Please enter a valid base price (minimum 1000 kip)');
      return false;
    }

    const extraPriceValue = parseInt(extraPrice.current?.value);
    if (!extraPriceValue || isNaN(extraPriceValue) || extraPriceValue < 1000) {
      setErrorMessage('Please enter a valid extra price (minimum 1000 kip)');
      return false;
    }

    if (!limitDistance.current?.value || isNaN(parseInt(limitDistance.current.value))) {
      setErrorMessage('Please enter a valid limit distance');
      return false;
    }

    if (!extraDistance.current?.value || isNaN(parseInt(extraDistance.current.value))) {
      setErrorMessage('Please enter a valid extra distance');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleOpenConfirmForm = (e: any) => {
    e.preventDefault();
    if (handleValidateForm()) {
      setOpenConfirm(true);
    }
  };

  const handleCloseConfirmForm = () => {
    setOpenConfirm(false);
  };

  const handleReset = () => {
    setSelectedData(null);
    areaName.current.value = '';
    basePrice.current.value = '';
    extraPrice.current.value = '';
    limitDistance.current.value = '';
    extraDistance.current.value = '';
  };

  const handleSubmit = () => {
    if (selectedData?.id) {
      handleEditDeliveryFee();
    } else {
      handleCreateDeliveryFee();
    }
  };

  const handleCreateDeliveryFee = async () => {
    try {
      setLoadingConfirmButton(true);
      const formData: any = {
        basePrice: parseInt(basePrice.current?.value),
        extraPrice: parseInt(extraPrice.current?.value),
        areaName: areaName.current?.value,
        limitDistance: parseInt(limitDistance.current?.value),
        extraDistance: parseInt(extraDistance.current?.value),
      };
      await axios.post(CREATE_DELIVERY_FEE, formData);
      await onFetchData();
      onClose();
      handleReset();
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenConfirm(false);
    }
  };

  const handleEditDeliveryFee = async () => {
    try {
      setLoadingConfirmButton(true);
      const formData: any = {
        basePrice: parseInt(basePrice.current?.value),
        extraPrice: parseInt(extraPrice.current?.value),
        areaName: areaName.current?.value,
        limitDistance: parseInt(limitDistance.current?.value),
        extraDistance: parseInt(extraDistance.current?.value),
      };
      await axios.patch(UPDATE_DELIVERY_FEE + selectedData?.id, formData);
      await onFetchData();
      onClose();
      handleReset();
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenConfirm(false);
    }
  };

  useEffect(() => {
    if (selectedData) {
      areaName.current.value = selectedData?.areaName;
      basePrice.current.value = selectedData?.basePrice.toString();
      extraPrice.current.value = selectedData?.extraPrice.toString();
      limitDistance.current.value = selectedData?.limitDistance.toString();
      extraDistance.current.value = selectedData?.extraDistance.toString();
    }
  }, []);

  return {
    distance,
    handleSelectedDistance,
    loadingConfirmButton,
    areaName,
    basePrice,
    extraPrice,
    limitDistance,
    extraDistance,
    handleOpenConfirmForm,
    handleCloseConfirmForm,
    openConfirm,
    errorMessage,
    handleSubmit,
    handleReset,
  };
};
